import type { SupportedLocale } from '@erp/shared';
export const documentEntryTranslations = {
  de: {
    stepsLabel: 'Beleg in drei Schritten',
    steps: {
      customer: 'Kunde & Rahmendaten',
      items: 'Artikel & Preise',
      review: 'Prüfen & speichern',
    },
    hints: {
      reviewExisting:
        'Prüfen Sie die Angaben. Speichern übernimmt Ihre Änderungen im bestehenden Beleg.',
      customer:
        'Kunden auswählen und die übernommenen Angaben prüfen. Pflichtangaben sind mit * markiert.',
      items: 'Artikel auswählen, Mengen eingeben und die ermittelten Preise prüfen.',
      review: 'Kunde, Positionen und Beträge prüfen. Speichern legt den Entwurf an.',
    },
    help: 'Hilfe zur Beleganlage',
    next: 'Weiter',
    back: 'Zurück',
    saveHint: 'Ihre Eingaben bleiben beim Wechsel erhalten.',
    saveDraft: 'Entwurf speichern',
    conditions: 'Konditionen, Zahlung & Steuer',
    reviewTitle: '{{type}} prüfen',
    editCustomer: 'Kundendaten ändern',
    editItems: 'Positionen ändern',
    paymentDays: '{{count}} Tage',
    reviewHint:
      'Prüfen Sie auch die Summen und Steuerangaben. Speichern führt die bestehenden Prüfungen aus; Versand und Folgeaktionen erfolgen anschließend.',
    newTitle: 'Neuer Beleg: {{type}}',
    createType: '{{type}} anlegen',
    createOrder: 'Auftrag anlegen',
    defaultsRequired:
      'Die Kundenvorgaben müssen zuerst vollständig geladen sein. Prüfen Sie die Kundenauswahl und versuchen Sie es erneut.',
    title: 'Kundendaten-Vorschau',
    loading: 'Kundenvorgaben werden geladen …',
    loadError: 'Kundenvorgaben konnten nicht geladen werden',
    preview: 'Vorschau der Kundendaten. Der Beleg wird erst beim Speichern angelegt.',
    unsaved: 'Ungespeicherte Änderungen verwerfen und den Beleg verlassen?',
  },
  en: {
    stepsLabel: 'Document in three steps',
    steps: {
      customer: 'Customer & details',
      items: 'Items & prices',
      review: 'Review & save',
    },
    hints: {
      reviewExisting: 'Review the details. Saving updates the existing document.',
      customer: 'Select a customer and check the inherited details. Required fields are marked *.',
      items: 'Select items, enter quantities and check the resolved prices.',
      review: 'Check the customer, items and amounts. Saving creates the draft.',
    },
    help: 'Document entry help',
    next: 'Next',
    back: 'Back',
    saveHint: 'Your entries are retained when switching steps.',
    saveDraft: 'Save draft',
    conditions: 'Terms, payment & tax',
    reviewTitle: 'Review {{type}}',
    editCustomer: 'Edit customer details',
    editItems: 'Edit items',
    paymentDays: '{{count}} days',
    reviewHint:
      'Check the totals and tax details too. Saving runs the existing checks; sending and follow-up actions are available afterwards.',
    newTitle: 'New document: {{type}}',
    createType: 'Create {{type}}',
    createOrder: 'Create order',
    defaultsRequired:
      'Customer defaults must finish loading first. Check the customer selection and try again.',
    title: 'Customer data preview',
    loading: 'Loading customer defaults …',
    loadError: 'Could not load customer defaults',
    preview: 'Customer data preview. The document is created only when you save.',
    unsaved: 'Discard unsaved changes and leave the document?',
  },
  fr: {
    stepsLabel: 'Document en trois étapes',
    steps: {
      customer: 'Client & informations',
      items: 'Articles & prix',
      review: 'Vérifier & enregistrer',
    },
    hints: {
      reviewExisting:
        'Vérifiez les informations. L’enregistrement met à jour le document existant.',
      customer:
        'Choisissez un client et vérifiez les informations reprises. Les champs obligatoires sont marqués *.',
      items: 'Choisissez les articles, saisissez les quantités et vérifiez les prix calculés.',
      review: 'Vérifiez le client, les lignes et les montants. L’enregistrement crée le brouillon.',
    },
    help: 'Aide à la création du document',
    next: 'Suivant',
    back: 'Retour',
    saveHint: 'Vos saisies sont conservées lors du changement d’étape.',
    saveDraft: 'Enregistrer le brouillon',
    conditions: 'Conditions, paiement & taxes',
    reviewTitle: 'Vérifier : {{type}}',
    editCustomer: 'Modifier les informations client',
    editItems: 'Modifier les lignes',
    paymentDays: '{{count}} jours',
    reviewHint:
      'Vérifiez aussi les totaux et les taxes. L’enregistrement exécute les contrôles existants ; l’envoi et les actions suivantes sont disponibles ensuite.',
    newTitle: 'Nouveau document : {{type}}',
    createType: 'Créer : {{type}}',
    createOrder: 'Créer une commande',
    defaultsRequired:
      'Les valeurs du client doivent être entièrement chargées. Vérifiez le client sélectionné et réessayez.',
    title: 'Aperçu des données client',
    loading: 'Chargement des valeurs du client …',
    loadError: 'Impossible de charger les valeurs du client',
    preview: 'Aperçu des données client. Le document est créé uniquement lors de l’enregistrement.',
    unsaved: 'Abandonner les modifications non enregistrées et quitter le document ?',
  },
  es: {
    stepsLabel: 'Documento en tres pasos',
    steps: {
      customer: 'Cliente y datos',
      items: 'Artículos y precios',
      review: 'Revisar y guardar',
    },
    hints: {
      reviewExisting: 'Revise los datos. Al guardar se actualiza el documento existente.',
      customer:
        'Seleccione un cliente y revise los datos heredados. Los campos obligatorios se marcan con *.',
      items: 'Seleccione artículos, introduzca cantidades y revise los precios calculados.',
      review: 'Revise el cliente, las líneas y los importes. Al guardar se crea el borrador.',
    },
    help: 'Ayuda para crear documentos',
    next: 'Siguiente',
    back: 'Atrás',
    saveHint: 'Sus datos se conservan al cambiar de paso.',
    saveDraft: 'Guardar borrador',
    conditions: 'Condiciones, pago e impuestos',
    reviewTitle: 'Revisar: {{type}}',
    editCustomer: 'Modificar datos del cliente',
    editItems: 'Modificar líneas',
    paymentDays: '{{count}} días',
    reviewHint:
      'Revise también los totales y los impuestos. Al guardar se ejecutan las comprobaciones existentes; el envío y las acciones posteriores estarán disponibles después.',
    newTitle: 'Nuevo documento: {{type}}',
    createType: 'Crear: {{type}}',
    createOrder: 'Crear pedido',
    defaultsRequired:
      'Primero deben cargarse todos los valores del cliente. Compruebe el cliente seleccionado y vuelva a intentarlo.',
    title: 'Vista previa de datos del cliente',
    loading: 'Cargando los valores del cliente …',
    loadError: 'No se pudieron cargar los valores del cliente',
    preview: 'Vista previa de los datos del cliente. El documento se crea al guardar.',
    unsaved: '¿Descartar los cambios sin guardar y salir del documento?',
  },
  tr: {
    stepsLabel: 'Üç adımda belge',
    steps: {
      customer: 'Müşteri ve bilgiler',
      items: 'Ürünler ve fiyatlar',
      review: 'Kontrol et ve kaydet',
    },
    hints: {
      reviewExisting: 'Bilgileri kontrol edin. Kaydetmek mevcut belgeyi günceller.',
      customer:
        'Müşteri seçin ve aktarılan bilgileri kontrol edin. Zorunlu alanlar * ile işaretlidir.',
      items: 'Ürünleri seçin, miktarları girin ve belirlenen fiyatları kontrol edin.',
      review: 'Müşteriyi, kalemleri ve tutarları kontrol edin. Kaydetmek taslak oluşturur.',
    },
    help: 'Belge oluşturma yardımı',
    next: 'İleri',
    back: 'Geri',
    saveHint: 'Adım değiştirirken girdileriniz korunur.',
    saveDraft: 'Taslağı kaydet',
    conditions: 'Koşullar, ödeme ve vergi',
    reviewTitle: 'Kontrol: {{type}}',
    editCustomer: 'Müşteri bilgilerini değiştir',
    editItems: 'Kalemleri değiştir',
    paymentDays: '{{count}} gün',
    reviewHint:
      'Toplamları ve vergi bilgilerini de kontrol edin. Kaydetme mevcut kontrolleri çalıştırır; gönderim ve sonraki işlemler daha sonra kullanılabilir.',
    newTitle: 'Yeni belge: {{type}}',
    createType: 'Oluştur: {{type}}',
    createOrder: 'Sipariş oluştur',
    defaultsRequired:
      'Önce müşteri varsayılanlarının tamamen yüklenmesi gerekir. Müşteri seçimini kontrol edip tekrar deneyin.',
    title: 'Müşteri verileri önizlemesi',
    loading: 'Müşteri varsayılanları yükleniyor …',
    loadError: 'Müşteri varsayılanları yüklenemedi',
    preview: 'Müşteri verilerinin önizlemesi. Belge yalnızca kaydettiğinizde oluşturulur.',
    unsaved: 'Kaydedilmemiş değişiklikler silinip belgeden çıkılsın mı?',
  },
} satisfies Record<SupportedLocale, Record<string, string | Record<string, string>>>;
